import { Subject, asyncScheduler, of, from, EMPTY } from 'rxjs';
import { switchMap, throttleTime, startWith, take, filter, map } from 'rxjs/operators';
import * as Ids from '@utils/ids';

import { printOrder } from '@utils/print/orderPrintService';

import { UnprocessedOrderType } from '@projects/Business/pages/OrderManage/shared/types';

import { getGrayStateByToken, GRAY_AB_TOKEN } from '@utils/grayAB';

import { FRAME_NAME, sendMessageToSubFrame } from '../utils/subFrames';

import bellService from '../service/bell';

import { ApplyRefundPushData, PushOrderUserRole } from '../connection/push/pushDataTypes';

import { getTagsByParentTag, setMenuTagList } from '../utils/getOrderMenuTags';

import { bootStateSubject } from '../boot';

import {
    getPrepMealOrderCount,
    getPrepDeliveryOrderCount,
    getRefundOrderCount,
    getFoodSafeProcess4PoiOrderCount,
    getPreOrderNearDeliveryTimeCount,
    updateOrderCount,
    getDeliveryExceptionOrderCount,
    getRemindOrderCount,
    getMealSlowOrderCount,
    getNewOrderCount,
    getAllOrderCount,
    getOtherExceptionOrderCount,
} from '../connection/eventHandler/order';
import * as API from '../connection/eventHandler/order/api';
import { addOrderNotification } from '../connection/eventHandler/pushNotification';

// eslint-disable-next-line waimaie/no-direct-access-localstorage
const orderUpdateThrottleSecondsCacheStr = localStorage.getItem('waimai_order_update_time');

/** 订单计数更新throttle间隔时间 */
let orderUpdateThrottleSeconds = 5;
if (orderUpdateThrottleSecondsCacheStr !== null) {
    const orderUpdateThrottleSecondsCache = parseInt(orderUpdateThrottleSecondsCacheStr, 10);
    if (!Number.isNaN(orderUpdateThrottleSecondsCache)) {
        orderUpdateThrottleSeconds = orderUpdateThrottleSecondsCache;
    }
}

const throttleConfig = { leading: true, trailing: true };

/** 更新全量订单计数 */
const orderAllCountUpdateSubject = new Subject<any>();
/** 更新全量订单计数-加节流 */
const orderAllCountUpdateDelaySubject = new Subject<any>();
/** 更新全量订单计数-加节流2秒 */
const orderAllCountUpdateDelaySubject2 = new Subject<any>();
/** 新订单计数更新队列 */
const orderNewOrderCountSubject = new Subject<any>();
/** 待出餐订单计数更新队列 */
const orderPrepCountUpdateSubject = new Subject<any>();
/** 待发配送订单计数更新队列 */
const orderDeliveryCountUpdateSubject = new Subject<any>();
/** 进行中订单计数更新队列 */
const orderProcessCountUpdateSubject = new Subject<any>();
/** 待处理退款订单计数更新队列 */
const orderRefundCountUpdateSubject = new Subject<ApplyRefundPushData | null>();
/** 待处理食安问题订单计数更新队列 */
const orderFoodSafeProcess4PoiCountUpdateSubject = new Subject<{ shouldUpdateCount: boolean }>();
/** 预订单临近送达时间提醒队列 */
const orderNearDeliveryTimeRemindSubject = new Subject<any>();
/** 待处理催单计数更新队列 */
const orderRemindCountUpdateSubject = new Subject<any>();
/** 出餐异常计数更新队列 */
const mealSlowOrderCountUpdateSubject = new Subject<any>();
/** 配送异常订单计数更新队列 */
const deliveryExceptionOrderCountUpdateSubject = new Subject<any>();
/** 其它异常订单(eg：ETA变更)计数更新队列 */
const otherExceptionOrderCountUpdateSubject = new Subject<any>();
/** 预订单备餐提醒打印队列 */
const preOrderPrintOnRemindSubject = new Subject<{
    user_role: PushOrderUserRole;
    wmPoiId: string;
    wmOrderViewId: string;
}>();

// eslint-disable-next-line import/no-mutable-exports
let menuTempInfo: any[] = [];

bootStateSubject
    .pipe(
        filter(state => state === 'readyToGo'),
        take(1),
    )
    .subscribe(() => {
        // 确保登录态ok后，开始初始化数据=
        /** 更新全量count */
        orderAllCountUpdateSubject
            .pipe(
                startWith(null),
                switchMap((val: any) => {
                    const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
                    if (
                        !UNIFIED_ORDER_GRAY ||
                        (Ids.getscIndex() !== '' && Ids.getscIndex() !== '0')
                    ) {
                        return EMPTY;
                    }
                    if (val && val.UpdateType) {
                        return of(val);
                    }
                    // 检查全局变量 menu 是否有值
                    if (menuTempInfo && menuTempInfo.length) {
                        return of(Object.assign(val || {}, { UpdateType: menuTempInfo })); // 如果有值，直接使用它
                    }

                    // 获取全量菜单
                    return from(API.getOrderMenuInfo()).pipe(
                        map(menus => {
                            // 订单tab 需要获取数量的tag，从第二层开始
                            const orderMenuTag = getTagsByParentTag(menus, 'order', 2) || [];
                            // 售后tab 需获取数量的tag，从第三层开始
                            const afterSaleMenuTag =
                                getTagsByParentTag(menus, 'afterSale', 3) || [];
                            menuTempInfo = [...orderMenuTag, ...afterSaleMenuTag];
                            setMenuTagList(menuTempInfo);
                            return Object.assign(val || {}, {
                                UpdateType: menuTempInfo,
                            });
                        }),
                    );
                }),
                switchMap((val: any) => {
                    return getAllOrderCount(val && val.UpdateType ? val.UpdateType : '');
                }),
            )
            .subscribe(obj => {
                if (obj) {
                    Object.keys(obj).forEach(key => {
                        updateOrderCount({
                            [key]: `${obj[key] || 0}`,
                            noticeInner: true,
                        });
                        if (obj[key] && obj[key] > 0) {
                            if (key === UnprocessedOrderType.Refund) {
                                bellService.playBell('refund');
                                addOrderNotification({
                                    content: '有用户申请退款啦！请尽快处理',
                                    href: '/new_fe/orderbusiness#/order/unprocessed?tab=refund',
                                });
                            } else if (key === UnprocessedOrderType.Remind) {
                                bellService.playBell('reminder');
                                addOrderNotification({
                                    content: '有用户催单啦！请尽快处理',
                                    href: '/new_fe/orderbusiness#/order/unprocessed?tab=remind',
                                });
                            } else if (key === UnprocessedOrderType.MealAbnormal) {
                                addOrderNotification({
                                    content: '有订单出餐异常，若已出餐，请您尽快处理',
                                });
                            } else if (key === UnprocessedOrderType.LogisticsAbnormal) {
                                addOrderNotification({
                                    content: '有订单配送异常！请尽快处理',
                                    href:
                                        '/new_fe/orderbusiness#/order/unprocessed?tab=logisticsAbnormal',
                                });
                            }
                        }
                    });
                }
            });
        /** 更新全量count 加延迟 */
        orderAllCountUpdateDelaySubject
            .pipe(
                startWith(null),
                throttleTime(1000 * orderUpdateThrottleSeconds, asyncScheduler, throttleConfig),
                switchMap((val: any) => {
                    const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
                    if (
                        !UNIFIED_ORDER_GRAY ||
                        (Ids.getscIndex() !== '' && Ids.getscIndex() !== '0')
                    ) {
                        return EMPTY;
                    }
                    if (val && val.UpdateType) {
                        return of(val);
                    }
                    // 检查全局变量 menu 是否有值
                    if (menuTempInfo && menuTempInfo.length) {
                        return of(Object.assign(val || {}, { UpdateType: menuTempInfo })); // 如果有值，直接使用它
                    }

                    // 获取全量菜单
                    return from(API.getOrderMenuInfo()).pipe(
                        map(menus => {
                            // 订单tab 需要获取数量的tag，从第二层开始
                            const orderMenuTag = getTagsByParentTag(menus, 'order', 2) || [];
                            // 售后tab 需获取数量的tag，从第三层开始
                            const afterSaleMenuTag =
                                getTagsByParentTag(menus, 'afterSale', 3) || [];
                            menuTempInfo = [...orderMenuTag, ...afterSaleMenuTag];
                            setMenuTagList(menuTempInfo);
                            return Object.assign(val || {}, {
                                UpdateType: menuTempInfo,
                            });
                        }),
                    );
                }),
                switchMap((val: any) => {
                    return getAllOrderCount(val && val.UpdateType ? val.UpdateType : '');
                }),
            )
            .subscribe(obj => {
                if (obj) {
                    Object.keys(obj).forEach(key => {
                        updateOrderCount({
                            [key]: `${obj[key] || 0}`,
                            noticeInner: true,
                        });
                        if (obj[key] && obj[key] > 0) {
                            if (key === UnprocessedOrderType.Refund) {
                                bellService.playBell('refund');
                                addOrderNotification({
                                    content: '有用户申请退款啦！请尽快处理',
                                    href: '/new_fe/orderbusiness#/order/unprocessed?tab=refund',
                                });
                            } else if (key === UnprocessedOrderType.Remind) {
                                bellService.playBell('reminder');
                                addOrderNotification({
                                    content: '有用户催单啦！请尽快处理',
                                    href: '/new_fe/orderbusiness#/order/unprocessed?tab=remind',
                                });
                            } else if (key === UnprocessedOrderType.MealAbnormal) {
                                addOrderNotification({
                                    content: '有订单出餐异常，若已出餐，请您尽快处理',
                                });
                            } else if (key === UnprocessedOrderType.LogisticsAbnormal) {
                                addOrderNotification({
                                    content: '有订单配送异常！请尽快处理',
                                    href:
                                        '/new_fe/orderbusiness#/order/unprocessed?tab=logisticsAbnormal',
                                });
                            }
                        }
                    });
                }
            });
        /** 更新全量count 加延迟 */
        orderAllCountUpdateDelaySubject2
            .pipe(
                startWith(null),
                throttleTime(2000, asyncScheduler, throttleConfig),
                switchMap((val: any) => {
                    const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
                    if (
                        !UNIFIED_ORDER_GRAY ||
                        (Ids.getscIndex() !== '' && Ids.getscIndex() !== '0')
                    ) {
                        return EMPTY;
                    }
                    if (val && val.UpdateType) {
                        return of(val);
                    }
                    // 检查全局变量 menu 是否有值
                    if (menuTempInfo && menuTempInfo.length) {
                        return of(Object.assign(val || {}, { UpdateType: menuTempInfo })); // 如果有值，直接使用它
                    }

                    // 获取全量菜单
                    return from(API.getOrderMenuInfo()).pipe(
                        map(menus => {
                            // 订单tab 需要获取数量的tag，从第二层开始
                            const orderMenuTag = getTagsByParentTag(menus, 'order', 2) || [];
                            // 售后tab 需获取数量的tag，从第三层开始
                            const afterSaleMenuTag =
                                getTagsByParentTag(menus, 'afterSale', 3) || [];
                            menuTempInfo = [...orderMenuTag, ...afterSaleMenuTag];
                            setMenuTagList(menuTempInfo);
                            return Object.assign(val || {}, {
                                UpdateType: menuTempInfo,
                            });
                        }),
                    );
                }),
                switchMap((val: any) => {
                    return getAllOrderCount(val && val.UpdateType ? val.UpdateType : '');
                }),
            )
            .subscribe(obj => {
                if (obj) {
                    Object.keys(obj).forEach(key => {
                        updateOrderCount({
                            [key]: `${obj[key] || 0}`,
                            noticeInner: true,
                        });
                        if (obj[key] && obj[key] > 0) {
                            if (key === UnprocessedOrderType.Refund) {
                                bellService.playBell('refund');
                                addOrderNotification({
                                    content: '有用户申请退款啦！请尽快处理',
                                    href: '/new_fe/orderbusiness#/order/unprocessed?tab=refund',
                                });
                            } else if (key === UnprocessedOrderType.Remind) {
                                bellService.playBell('reminder');
                                addOrderNotification({
                                    content: '有用户催单啦！请尽快处理',
                                    href: '/new_fe/orderbusiness#/order/unprocessed?tab=remind',
                                });
                            } else if (key === UnprocessedOrderType.MealAbnormal) {
                                addOrderNotification({
                                    content: '有订单出餐异常，若已出餐，请您尽快处理',
                                });
                            } else if (key === UnprocessedOrderType.LogisticsAbnormal) {
                                addOrderNotification({
                                    content: '有订单配送异常！请尽快处理',
                                    href:
                                        '/new_fe/orderbusiness#/order/unprocessed?tab=logisticsAbnormal',
                                });
                            }
                        }
                    });
                }
            });
        /** 更新新订单计数 */
        orderNewOrderCountSubject
            .pipe(
                startWith(null),
                switchMap(() => {
                    return getNewOrderCount();
                }),
            )
            .subscribe(count => {
                if (count !== null) {
                    updateOrderCount({
                        [UnprocessedOrderType.NewOrder]: `${count}`,
                        noticeInner: true,
                    });
                }
            });
        /** 更新待出餐订单计数 */
        orderPrepCountUpdateSubject
            .pipe(
                startWith(null),
                throttleTime(1000 * orderUpdateThrottleSeconds, asyncScheduler, throttleConfig),
                switchMap(val => {
                    /** 初始化不更新，通过新订单轮询接口更新 */
                    if (!val) return of(null);
                    return getPrepMealOrderCount();
                }),
            )
            .subscribe(count => {
                if (count !== null) {
                    updateOrderCount({
                        [UnprocessedOrderType.PrepMeal]: `${count}`,
                        noticeInner: true,
                    });
                }
            });

        /** 更新待发配送订单计数 */
        orderDeliveryCountUpdateSubject
            .pipe(
                startWith(null),
                throttleTime(1000 * orderUpdateThrottleSeconds, asyncScheduler, throttleConfig),
                switchMap(val => {
                    /** 初始化不更新，通过新订单轮询接口更新 */
                    if (!val) return of(null);
                    return getPrepDeliveryOrderCount();
                }),
            )
            .subscribe(count => {
                if (count !== null) {
                    updateOrderCount({
                        [UnprocessedOrderType.PrepDelivery]: `${count || 0}`,
                        noticeInner: true,
                    });
                }
            });

        /** 更新进行中订单计数(进行中订单本身不展示数量，仅利用计数机制通知内层页面刷新订单列表) */
        orderProcessCountUpdateSubject
            .pipe(throttleTime(1000 * orderUpdateThrottleSeconds, asyncScheduler, throttleConfig))
            .subscribe(() => {
                updateOrderCount({
                    [UnprocessedOrderType.Process]: `0`,
                    noticeInner: true,
                });
            });

        /** 更新待处理退款订单计数 */
        orderRefundCountUpdateSubject
            .pipe(
                switchMap(val => {
                    /** 初始化不更新，通过新订单轮询接口更新 */
                    if (!val) return of(null);
                    return getRefundOrderCount();
                }),
            )
            .subscribe(toRefundCount => {
                if (toRefundCount != null) {
                    updateOrderCount({
                        [UnprocessedOrderType.Refund]: `${toRefundCount}`,
                        noticeInner: true,
                    });

                    if (toRefundCount > 0) {
                        bellService.playBell('refund');

                        addOrderNotification({
                            content: '有用户申请退款啦！请尽快处理',
                            href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                                ? '/new_fe/orderbusiness#/order/unprocessed?tab=refund'
                                : '/new_fe/business#/order/unprocessed?tab=refund',
                        });
                    }
                }
            });

        orderFoodSafeProcess4PoiCountUpdateSubject
            .pipe(
                startWith({ shouldUpdateCount: false }),
                switchMap(value => {
                    if (!value?.shouldUpdateCount) return of(null); // 初始化不更新，通过新订单轮询接口更新
                    return getFoodSafeProcess4PoiOrderCount();
                }),
            )
            .subscribe(count => {
                if (count !== null) {
                    updateOrderCount({
                        [UnprocessedOrderType.FoodSafeProcess4Poi]: `${count}`,
                        noticeInner: true,
                    });
                }
            });

        orderNearDeliveryTimeRemindSubject
            .pipe(
                throttleTime(3000, asyncScheduler, throttleConfig),
                switchMap(getPreOrderNearDeliveryTimeCount),
            )
            .subscribe(count => {
                if (count !== null && count > 0) {
                    bellService.playBell('preorder');
                    addOrderNotification({
                        content: `您有 ${count} 个预订单临近送达时间，请注意备货并配送`,
                        href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                            ? '/new_fe/orderbusiness#/order/history'
                            : '/new_fe/business#/order/history',
                    });
                }
            });
        orderRemindCountUpdateSubject
            .pipe(
                startWith(null),
                switchMap(val => {
                    /** 初始化不更新，通过新订单轮询接口更新 */
                    if (!val) return of(null);
                    return getRemindOrderCount();
                }),
            )
            .subscribe(count => {
                if (count !== null) {
                    updateOrderCount({
                        [UnprocessedOrderType.Remind]: `${count}`,
                        noticeInner: true,
                    });
                    if (count > 0) {
                        bellService.playBell('reminder');
                        addOrderNotification({
                            content: '有用户催单啦！请尽快处理',
                            href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                                ? '/new_fe/orderbusiness#/order/unprocessed?tab=remind'
                                : '/new_fe/business#/order/unprocessed?tab=remind',
                        });
                    }
                }
            });

        mealSlowOrderCountUpdateSubject
            .pipe(
                startWith(null),
                switchMap(val => {
                    /** 初始化不更新，通过新订单轮询接口更新 */
                    if (!val) return of(null);
                    return getMealSlowOrderCount();
                }),
            )
            .subscribe(count => {
                if (count !== null) {
                    updateOrderCount({
                        [UnprocessedOrderType.Unnormal]: `${count}`,
                        noticeInner: true,
                    });
                    if (count > 0) {
                        addOrderNotification({
                            content: '有订单出餐异常，若已出餐，请您尽快处理',
                        });
                    }
                }
            });

        deliveryExceptionOrderCountUpdateSubject
            .pipe(
                startWith(null),
                // switchMap(getDeliveryExceptionOrderCount)
                switchMap(val => {
                    /** 初始化不更新，通过新订单轮询接口更新 */
                    if (!val) return of(null);
                    return getDeliveryExceptionOrderCount();
                }),
            )
            .subscribe(count => {
                if (count !== null) {
                    updateOrderCount({
                        [UnprocessedOrderType.DeliveryException]: `${count}`,
                        noticeInner: true,
                    });
                    if (count > 0) {
                        addOrderNotification({
                            content: '有订单配送异常！请尽快处理',
                            href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                                ? '/new_fe/orderbusiness#/order/unprocessed?tab=logisticsAbnormal'
                                : '/new_fe/business#/order/unprocessed?tab=deliveryException',
                        });
                    }
                }
            });

        otherExceptionOrderCountUpdateSubject
            .pipe(
                startWith(null),
                // switchMap(getDeliveryExceptionOrderCount)
                switchMap(val => {
                    /** 初始化不更新，通过新订单轮询接口更新 */
                    if (!val) return of(null);
                    return getOtherExceptionOrderCount();
                }),
            )
            .subscribe(count => {
                if (count !== null) {
                    updateOrderCount({
                        [UnprocessedOrderType.Other]: `${count}`,
                        noticeInner: true,
                    });
                }
            });

        preOrderPrintOnRemindSubject.subscribe(
            ({ user_role: userRole, wmOrderViewId, wmPoiId }) => {
                const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
                let getOrderDetailFn;

                if (UNIFIED_ORDER_GRAY) {
                    getOrderDetailFn = API.getOrderDetailFromPushNewUnified;
                } else if (userRole === PushOrderUserRole.NORMAL) {
                    getOrderDetailFn = API.getOrderDetailFromPushNew;
                } else {
                    getOrderDetailFn = API.getOrderDetailFromPush;
                }
                getOrderDetailFn(wmOrderViewId, wmPoiId, userRole).then(order => {
                    if (!order) return;

                    printOrder({
                        wmPoiId: order.wm_poi_id,
                        wmOrderViewId: order.wm_order_id_view_str,
                        businessType: order.businessType,
                        // @ts-ignore
                        orderType: order.orderType,
                    }).then(latestPrintCount => {
                        sendMessageToSubFrame(FRAME_NAME.HASH, {
                            type: 'updatePrintCount',
                            data: {
                                orderViewId: wmOrderViewId,
                                latestPrintCount,
                            },
                        });
                    });
                });
            },
        );
    });

export {
    orderPrepCountUpdateSubject,
    orderDeliveryCountUpdateSubject,
    orderProcessCountUpdateSubject,
    orderRefundCountUpdateSubject,
    orderFoodSafeProcess4PoiCountUpdateSubject,
    orderNearDeliveryTimeRemindSubject,
    orderRemindCountUpdateSubject,
    mealSlowOrderCountUpdateSubject,
    orderNewOrderCountSubject,
    otherExceptionOrderCountUpdateSubject,
    preOrderPrintOnRemindSubject,
    orderAllCountUpdateSubject,
    orderAllCountUpdateDelaySubject,
    orderAllCountUpdateDelaySubject2,
    menuTempInfo,
};
